import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api';
import { fieldService, AgentLocation } from '@/services/fieldService';

interface DashboardSummary {
    totalOperations: number;
    activeOperations: number;
    criticalNodes: number;
    pendingAlerts: number;
    systemStatus: {
        synchronizationEngine: string;
        database: string;
        api: string;
        mediaMtx: string;
        mumble: string;
        signalBridge: string;
    };
    signalMetrics: {
        activeGroups: number;
        interceptedFiles: number;
        e2eeStatus: string;
    };
    liveStreamActive: boolean;
    recentActivity: Array<{
        type: string;
        description: string;
        time: string;
    }>;
    // Nuevas métricas OPC
    opcMetrics: {
        nivel1: number;
        nivel2: number;
        nivel3: number;
    };
    agencyMetrics: Array<{
        agency: string;
        count: number;
    }>;
    supportServicesMetrics: Array<{
        service: string;
        count: number;
        icon: string;
    }>;
}


export default function DashboardPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [summary, setSummary] = useState<DashboardSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [agents, setAgents] = useState<AgentLocation[]>([]);

    useEffect(() => {
        loadDashboard();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadAgents = async () => {
            try {
                const data = await fieldService.getAgents();
                if (!cancelled) setAgents(data);
            } catch (error) {
                console.error('Error loading field agents:', error);
            }
        };
        loadAgents();
        const interval = setInterval(loadAgents, 15000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, []);

    const loadDashboard = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/v1/dashboard/summary');
            setSummary(response.data);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    const totalOpc = (summary?.opcMetrics.nivel1 || 0) + (summary?.opcMetrics.nivel2 || 0) + (summary?.opcMetrics.nivel3 || 0);
    const nivel1Pct = totalOpc ? ((summary?.opcMetrics.nivel1 || 0) / totalOpc * 100).toFixed(0) : 0;
    const nivel2Pct = totalOpc ? ((summary?.opcMetrics.nivel2 || 0) / totalOpc * 100).toFixed(0) : 0;
    const nivel3Pct = totalOpc ? ((summary?.opcMetrics.nivel3 || 0) / totalOpc * 100).toFixed(0) : 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">{t('dashboard.title')}</h1>
                    <p className="text-gray-400 mt-1">{t('dashboard.subtitle')}</p>
                </div>
                <div className="text-sm text-gray-400">
                    {t('dashboard.lastUpdate', { time: new Date().toLocaleTimeString('es-CL') })}
                </div>
            </div>

            {/* Métricas Principales */}
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 hover:border-blue-500/50 transition-colors">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm">{t('dashboard.totalOperations')}</p>
                            <p className="text-3xl font-bold text-white mt-2">{summary?.totalOperations}</p>
                            <p className="text-green-400 text-xs mt-1">{t('dashboard.vsLastMonth')}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                            <span className="text-2xl">📋</span>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 hover:border-green-500/50 transition-colors">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm">{t('dashboard.activeOps')}</p>
                            <p className="text-3xl font-bold text-green-400 mt-2">{summary?.activeOperations}</p>
                            <p className="text-gray-400 text-xs mt-1">{t('dashboard.ongoingNow')}</p>
                        </div>
                        <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                            <span className="text-2xl">🎯</span>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 hover:border-yellow-500/50 transition-colors">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm">{t('dashboard.criticalNodes')}</p>
                            <p className="text-3xl font-bold text-yellow-400 mt-2">{summary?.criticalNodes}</p>
                            <p className="text-yellow-400 text-xs mt-1">{t('dashboard.requireAttention')}</p>
                        </div>
                        <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                            <span className="text-2xl">⚠️</span>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-800/50 rounded-xl p-5 border border-gray-700/50 hover:border-red-500/50 transition-colors">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm">{t('dashboard.pendingAlerts')}</p>
                            <p className="text-3xl font-bold text-red-400 mt-2">{summary?.pendingAlerts}</p>
                            <p className="text-red-400 text-xs mt-1">{t('dashboard.conflictsDetected')}</p>
                        </div>
                        <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                            <span className="text-2xl">🔔</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sección: Distribución por Nivel OPC */}
            <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        {t('dashboard.opcMatrixTitle')}
                    </h3>

                    {/* Gráfico de Barras Visual */}
                    <div className="space-y-4">
                        {/* Nivel I */}
                        <div className="flex items-center gap-4">
                            <div className="w-20 text-sm font-medium text-green-400">{t('dashboard.level1')}</div>
                            <div className="flex-1 bg-gray-700/50 rounded-full h-8 overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-green-600 to-green-400 h-full rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                                    style={{ width: `${nivel1Pct}%` }}
                                >
                                    <span className="text-xs font-bold text-white">{summary?.opcMetrics.nivel1}</span>
                                </div>
                            </div>
                            <div className="w-12 text-right text-sm text-gray-400">{nivel1Pct}%</div>
                        </div>

                        {/* Nivel II */}
                        <div className="flex items-center gap-4">
                            <div className="w-20 text-sm font-medium text-yellow-400">{t('dashboard.level2')}</div>
                            <div className="flex-1 bg-gray-700/50 rounded-full h-8 overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-yellow-600 to-yellow-400 h-full rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                                    style={{ width: `${nivel2Pct}%` }}
                                >
                                    <span className="text-xs font-bold text-white">{summary?.opcMetrics.nivel2}</span>
                                </div>
                            </div>
                            <div className="w-12 text-right text-sm text-gray-400">{nivel2Pct}%</div>
                        </div>

                        {/* Nivel III */}
                        <div className="flex items-center gap-4">
                            <div className="w-20 text-sm font-medium text-red-400">{t('dashboard.level3')}</div>
                            <div className="flex-1 bg-gray-700/50 rounded-full h-8 overflow-hidden">
                                <div
                                    className="bg-gradient-to-r from-red-600 to-red-400 h-full rounded-full flex items-center justify-end pr-3 transition-all duration-500"
                                    style={{ width: `${nivel3Pct}%` }}
                                >
                                    <span className="text-xs font-bold text-white">{summary?.opcMetrics.nivel3}</span>
                                </div>
                            </div>
                            <div className="w-12 text-right text-sm text-gray-400">{nivel3Pct}%</div>
                        </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-700/50">
                        <div className="flex justify-between text-sm">
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                                <span className="text-gray-400">{t('dashboard.level1Legend')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                                <span className="text-gray-400">{t('dashboard.level2Legend')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                                <span className="text-gray-400">{t('dashboard.level3Legend')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Operaciones Nivel III (Críticas) */}
                <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
                    <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
                        {t('dashboard.level3OpsTitle')}
                    </h3>
                    <div className="text-center py-4">
                        <div className="text-5xl font-bold text-red-400">{summary?.opcMetrics.nivel3}</div>
                        <div className="text-gray-400 text-sm mt-2">{t('dashboard.highComplexityOps')}</div>
                    </div>
                    <div className="space-y-2 mt-4">
                        <div className="bg-red-500/20 rounded-lg p-3 flex items-center gap-3">
                            <span className="text-xl">⚠️</span>
                            <div>
                                <div className="text-sm text-white font-medium">CAR-2024-092</div>
                                <div className="text-xs text-red-300">Operativo Antidrogas - 4 agencias</div>
                            </div>
                        </div>
                        <div className="bg-red-500/20 rounded-lg p-3 flex items-center gap-3">
                            <span className="text-xl">⚠️</span>
                            <div>
                                <div className="text-sm text-white font-medium">PDI-2024-078</div>
                                <div className="text-xs text-red-300">Desarticulación Banda - Internacional</div>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/operations')}
                        className="w-full mt-4 bg-red-500/20 hover:bg-red-500/30 text-red-400 py-2 rounded-lg text-sm transition-colors"
                    >
                        {t('dashboard.viewAll')}
                    </button>
                </div>
            </div>

            {/* Segunda fila: Agencias y Servicios de Apoyo */}
            <div className="grid grid-cols-2 gap-4">
                {/* Operaciones por Agencia */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        {t('dashboard.opsByAgency')}
                    </h3>
                    <div className="space-y-3">
                        {summary?.agencyMetrics.map((item, index) => {
                            const maxCount = Math.max(...(summary?.agencyMetrics.map(a => a.count) || [1]));
                            const percentage = (item.count / maxCount * 100).toFixed(0);
                            const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-cyan-500'];
                            return (
                                <div key={item.agency} className="flex items-center gap-3">
                                    <div className="w-28 text-sm text-gray-300 truncate">{item.agency}</div>
                                    <div className="flex-1 bg-gray-700/50 rounded-full h-6 overflow-hidden">
                                        <div
                                            className={`${colors[index % colors.length]} h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500`}
                                            style={{ width: `${percentage}%` }}
                                        >
                                            <span className="text-xs font-bold text-white">{item.count}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Servicios de Apoyo Más Utilizados */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        {t('dashboard.mostRequestedSupport')}
                    </h3>
                    <div className="space-y-3">
                        {summary?.supportServicesMetrics.map((item) => {
                            const maxCount = Math.max(...(summary?.supportServicesMetrics.map(s => s.count) || [1]));
                            const percentage = (item.count / maxCount * 100).toFixed(0);
                            return (
                                <div key={item.service} className="flex items-center gap-3">
                                    <span className="text-xl">{item.icon}</span>
                                    <div className="w-32 text-sm text-gray-300 truncate">{item.service}</div>
                                    <div className="flex-1 bg-gray-700/50 rounded-full h-6 overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-orange-600 to-orange-400 h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                            style={{ width: `${percentage}%` }}
                                        >
                                            <span className="text-xs font-bold text-white">{item.count}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Tercera fila: Estado del Sistema y Actividad Reciente */}
            <div className="grid grid-cols-3 gap-4">
                {/* Estado del Sistema */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        {t('dashboard.systemStatus')}
                    </h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                                <span className="text-gray-300">{t('dashboard.conflictDetection')}</span>
                            </div>
                            <span className="text-green-400 text-sm font-medium">{summary?.systemStatus.synchronizationEngine}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                                <span className="text-gray-300">{t('dashboard.database')}</span>
                            </div>
                            <span className="text-green-400 text-sm font-medium">{summary?.systemStatus.database}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                                <span className="text-gray-300">{t('dashboard.mainApi')}</span>
                            </div>
                            <span className="text-green-400 text-sm font-medium">{summary?.systemStatus.api}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-indigo-400 rounded-full animate-pulse"></span>
                                <span className="text-gray-300">{t('dashboard.signalBridge')}</span>
                            </div>
                            <span className="text-indigo-400 text-sm font-medium">{summary?.systemStatus.signalBridge}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-red-400 rounded-full animate-pulse"></span>
                                <span className="text-gray-300">{t('dashboard.mediaMtxEngine')}</span>
                            </div>
                            <span className="text-red-400 text-sm font-medium">{summary?.systemStatus.mediaMtx}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></span>
                                <span className="text-gray-300">{t('dashboard.mumbleServer')}</span>
                            </div>
                            <span className="text-yellow-400 text-sm font-medium">{summary?.systemStatus.mumble}</span>
                        </div>
                    </div>
                </div>

                {/* Comunicaciones Signal */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                    <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            {t('dashboard.signalIntelligence')}
                        </h3>
                        {summary?.signalMetrics.e2eeStatus === 'SECURE' ? (
                            <span className="bg-green-500/20 text-green-400 text-[10px] px-2 py-1 rounded-md font-bold border border-green-500/30">{t('dashboard.e2eeActive')}</span>
                        ) : (
                            <span className="bg-red-500/20 text-red-400 text-[10px] px-2 py-1 rounded-md font-bold border border-red-500/30">{t('dashboard.e2eeFailure')}</span>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <div className="bg-indigo-900/20 border border-indigo-500/20 rounded-lg p-4 text-center">
                            <div className="text-2xl font-black text-indigo-400">{summary?.signalMetrics.activeGroups}</div>
                            <div className="text-xs text-gray-400 mt-1 uppercase font-bold">{t('dashboard.tacticalGroups')}</div>
                        </div>
                        <div className="bg-indigo-900/20 border border-indigo-500/20 rounded-lg p-4 text-center">
                            <div className="text-2xl font-black text-indigo-400">{summary?.signalMetrics.interceptedFiles}</div>
                            <div className="text-xs text-gray-400 mt-1 uppercase font-bold">{t('dashboard.docsMedia')}</div>
                        </div>
                    </div>
                    <button onClick={() => navigate('/signal')} className="w-full mt-5 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 border border-indigo-500/30 py-2 rounded-lg text-sm transition-colors text-center font-bold">
                        {t('dashboard.openSignalConsole')}
                    </button>
                </div>

                {/* Actividad Reciente */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        {t('dashboard.recentActivity')}
                    </h3>
                    <div className="space-y-3">
                        {summary?.recentActivity.slice(0, 4).map((activity, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors">
                                <span className="text-xl">
                                    {activity.type === 'operation' ? '📋' :
                                        activity.type === 'alert' ? '🔔' :
                                            activity.type === 'resolution' ? '✅' : '📌'}
                                </span>
                                <div className="flex-1">
                                    <p className="text-white text-sm">{activity.description}</p>
                                    <p className="text-gray-400 text-xs mt-1">{activity.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Cuarta Fila: Mini Centro en Vivo y Botones de Alerta */}
            <div className="grid grid-cols-3 gap-4">
                {/* Mini Reproductor Centro en Vivo */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 col-span-1 border-l-4 border-l-red-500 flex flex-col justify-between">
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                {t('dashboard.liveRadar')}
                            </h3>
                            {summary?.liveStreamActive ? (
                                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                            ) : (
                                <span className="w-3 h-3 bg-gray-500 rounded-full"></span>
                            )}
                        </div>

                        <div className="bg-black/50 aspect-video rounded-lg border border-white/5 flex flex-col items-center justify-center relative overflow-hidden">
                            {summary?.liveStreamActive ? (
                                <div className="absolute inset-0 bg-red-900/20 flex flex-col items-center justify-center">
                                    <div className="text-red-500 mb-2">
                                        <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <span className="text-red-400 font-bold text-xs uppercase tracking-widest animate-pulse">{t('dashboard.interceptedSignalActive')}</span>
                                </div>
                            ) : (
                                <>
                                    <div className="w-16 h-16 border-4 border-gray-700 rounded-full flex items-center justify-center mb-3">
                                        <div className="w-1.5 h-8 bg-gray-500 rounded-full origin-bottom animate-spin" style={{ animationDuration: '3s' }}></div>
                                    </div>
                                    <span className="text-gray-500 font-mono text-[10px] uppercase tracking-widest">{t('dashboard.patrollingSpectrum')}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Acciones Rápidas (Ahora toman las 2 columnas restantes de esta fila) */}
                <div className="col-span-2 grid grid-cols-2 gap-4">
                    <button
                        onClick={() => navigate('/live')}
                        className="bg-red-600/20 hover:bg-red-600 border border-red-500/50 text-white p-6 rounded-xl flex flex-col items-center justify-center gap-3 transition-colors group"
                    >
                        <span className="text-4xl group-hover:scale-110 transition-transform">🎥</span>
                        <div className="text-center">
                            <div className="font-bold text-lg uppercase tracking-wider text-red-300 group-hover:text-white">{t('dashboard.liveCenter')}</div>
                            <div className="text-xs text-red-400 group-hover:text-red-100 mt-1 opacity-80">{t('dashboard.tacticalStreaming')}</div>
                        </div>
                    </button>

                    <button
                        onClick={() => navigate('/signal')}
                        className="bg-indigo-600/20 hover:bg-indigo-600 border border-indigo-500/50 text-white p-6 rounded-xl flex flex-col items-center justify-center gap-3 transition-colors group"
                    >
                        <span className="text-4xl group-hover:scale-110 transition-transform">💬</span>
                        <div className="text-center">
                            <div className="font-bold text-lg uppercase tracking-wider text-indigo-300 group-hover:text-white">{t('dashboard.signalCommand')}</div>
                            <div className="text-xs text-indigo-400 group-hover:text-indigo-100 mt-1 opacity-80">{t('dashboard.e2eeMultimedia')}</div>
                        </div>
                    </button>

                    <button
                        onClick={() => navigate('/operations')}
                        className="bg-blue-600/40 hover:bg-blue-600 border border-blue-500/50 text-white p-4 rounded-xl flex items-center gap-3 transition-colors"
                    >
                        <span className="text-2xl">📋</span>
                        <span className="font-medium">{t('dashboard.newOperation')}</span>
                    </button>

                    <button
                        onClick={() => navigate('/map')}
                        className="bg-green-600/40 hover:bg-green-600 border border-green-500/50 text-white p-4 rounded-xl flex items-center gap-3 transition-colors"
                    >
                        <span className="text-2xl">🗺️</span>
                        <span className="font-medium">{t('dashboard.operationalMap')}</span>
                    </button>
                </div>
            </div>

            {/* Unidades en Terreno */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50">
                    <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            {t('dashboard.fieldUnits')}
                        </h3>
                        <span className="bg-blue-500/20 text-blue-400 text-[10px] px-2 py-1 rounded-md font-bold border border-blue-500/30">
                            {t('dashboard.activeCount', { count: agents.length })}
                        </span>
                    </div>
                    {agents.length === 0 ? (
                        <div className="py-8 text-center text-gray-500 text-sm">
                            {t('dashboard.noUnitsReporting')}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {agents.slice(0, 6).map((a) => (
                                <div
                                    key={a.userId}
                                    className="flex items-center justify-between p-2.5 bg-gray-700/30 rounded-lg"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse shrink-0"></span>
                                        <span className="text-sm text-white font-medium truncate">{a.callsign}</span>
                                    </div>
                                    <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                                        {new Date(a.lastUpdate).toLocaleTimeString('es-CL')}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}
